import { memo, useCallback } from "react";
import ModalWrapper from "..";
import { useTranslation } from "react-i18next";
import { Button } from "../../button";
import { CheckCheck } from "lucide-react";
import { cn } from "../../../utils/cn";
import { EVACUATION_EVENT_PARTICIPANTS_STATUSES } from "../../../constants/common";
import { useForm } from "react-hook-form";
import { useUpdateEvacuationEventParticipantStatusByQR } from "../../../hooks/emergency-evacuation/mutation";
import toast from "react-hot-toast";

interface MusterStationScannedQRDetailsModalProps {
  musterPointId: number;
  appointment: any;
  isOpen: boolean;
  onOpenChange: () => void;
}

const MusterStationScannedQRDetailsModal = ({
  musterPointId,
  appointment,
  isOpen,
  onOpenChange,
}: MusterStationScannedQRDetailsModalProps) => {
  const { t, i18n } = useTranslation();

  const { handleSubmit, setValue } = useForm();

  const {
    mutateAsync: UpdateEvacuationEventParticipantStatusByQR,
    isPending: isUpdateEvacuationEventParticipantStatusByQRLoading,
  } = useUpdateEvacuationEventParticipantStatusByQR();

  const onSubmit = useCallback(async () => {
    try {
      const evacuationId = appointment?.musterPoints?.find(
        (item: { n_MusterPointID: number }) =>
          item.n_MusterPointID === musterPointId,
      )?.n_EvacuationID;

      const body = {
        n_EvacuationID: evacuationId,
        n_MusterPointID: musterPointId,
      };

      await UpdateEvacuationEventParticipantStatusByQR(body);
      toast.success(t("participantMarkedSafe"));
    } catch (error) {
      toast.error(t("somethingWentWrong"));
    } finally {
      onOpenChange();
      setValue("musterPoint", "");
    }
  }, [
    appointment?.musterPoints,
    musterPointId,
    UpdateEvacuationEventParticipantStatusByQR,
    t,
    onOpenChange,
    setValue,
  ]);

  return (
    <ModalWrapper
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      contentStyles="md:!max-w-md"
      descriptionStyles="relative"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className=" flex flex-col items-center justify-center gap-3 w-full"
      >
        <div
          className={cn(
            "absolute top-3 sm:top-2.5 right-8 rounded-full px-1 sm:px-2",
            appointment?.n_EvacuationStatus ===
              EVACUATION_EVENT_PARTICIPANTS_STATUSES.MISSING
              ? "bg-red-500"
              : appointment?.n_EvacuationStatus ===
                  EVACUATION_EVENT_PARTICIPANTS_STATUSES.CANCELLED
                ? "bg-orange-600"
                : appointment?.n_EvacuationStatus ===
                    EVACUATION_EVENT_PARTICIPANTS_STATUSES.SAFE
                  ? "bg-green-600"
                  : "bg-blue-600",
          )}
        >
          <p className="text-sm text-white">
            {i18n.language === "en"
              ? appointment?.s_EvacuationStatus_En
              : appointment?.s_EvacuationStatus_Ar}
          </p>
        </div>

        <img
          src={`data:${appointment?.s_PhotoContentType};base64,${appointment?.s_Photo}`}
          alt="user"
          className="w-28 h-28 sm:w-32 sm:h-32 rounded-full border"
        />

        <div className="flex flex-col items-center justify-center">
          <h1 className="text-2xl font-semibold">{appointment?.s_FullName}</h1>
          <p className="text-sm my-2 text-gray-600">
            {i18n.language === "en"
              ? appointment?.s_PersonType_En
              : appointment?.s_PersonType_Ar}
          </p>
        </div>

        {appointment?.n_EvacuationStatus ===
          EVACUATION_EVENT_PARTICIPANTS_STATUSES.MISSING && (
          <Button
            isLoading={isUpdateEvacuationEventParticipantStatusByQRLoading}
            Icon={<CheckCheck className="w-5" />}
            className="max-w-max px-10 py-2 uppercase bg-green-600 hover:bg-green-700"
          >
            {t("safe")}
          </Button>
        )}
      </form>
    </ModalWrapper>
  );
};

export default memo(MusterStationScannedQRDetailsModal);
